import type { CropRect, EditImageRequest, FormatOutput, GeneratedAsset } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Crop, Download, Images, ImageUp, Save, SlidersHorizontal } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useEditImage } from '~/features/ai-studio/hooks'
import { AiProcessingOverlay } from '~/features/shared/components/ai-processing-overlay'
import { AssetLibraryPicker } from '~/features/shared/components/asset-library-picker'
import { ImageUploadField } from '~/features/shared/components/image-upload-field'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { Card, CardContent } from '~/shared/ui/card'
import { Checkbox } from '~/shared/ui/checkbox'
import { Label } from '~/shared/ui/label'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'
import { PageHeader } from '~/widgets/page-header'

const FIXED_ASPECTS: {
  key: string
  label: () => string
  value: number
}[] = [
  { key: 'square', label: m.cropAspectSquare, value: 1 },
  { key: 'portrait', label: m.cropAspectPortrait, value: 4 / 5 },
  { key: 'story', label: m.cropAspectStory, value: 9 / 16 },
  { key: 'landscape', label: m.cropAspectLandscape, value: 16 / 9 },
  { key: 'cover', label: m.cropAspectCover, value: 1536 / 1024 },
]

const OUTPUTS: FormatOutput[] = ['jpeg', 'png', 'webp']

interface Adjustments {
  brightness: number
  contrast: number
  saturation: number
  grayscale: boolean
  normalize: boolean
}

const DEFAULT_ADJUSTMENTS: Adjustments = {
  brightness: 1,
  contrast: 1,
  saturation: 1,
  grayscale: false,
  normalize: false,
}

const PRESETS: { key: string; label: () => string; value: Adjustments }[] = [
  { key: 'reset', label: m.filterPresetReset, value: DEFAULT_ADJUSTMENTS },
  {
    key: 'auto',
    label: m.filterPresetAuto,
    value: { ...DEFAULT_ADJUSTMENTS, normalize: true },
  },
  {
    key: 'bw',
    label: m.filterPresetBw,
    value: { ...DEFAULT_ADJUSTMENTS, grayscale: true },
  },
  {
    key: 'warm',
    label: m.filterPresetWarm,
    value: { ...DEFAULT_ADJUSTMENTS, brightness: 1.05, saturation: 1.15 },
  },
  {
    key: 'cool',
    label: m.filterPresetCool,
    value: { ...DEFAULT_ADJUSTMENTS, saturation: 0.85 },
  },
  {
    key: 'punch',
    label: m.filterPresetPunch,
    value: { ...DEFAULT_ADJUSTMENTS, contrast: 1.2, saturation: 1.1 },
  },
]

function sameAdjustments(a: Adjustments, b: Adjustments): boolean {
  return (
    a.brightness === b.brightness &&
    a.contrast === b.contrast &&
    a.saturation === b.saturation &&
    a.grayscale === b.grayscale &&
    a.normalize === b.normalize
  )
}

type EditorTab = 'crop' | 'adjust'

export function EditPictureTool() {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const editImage = useEditImage(businessId)

  const cropTabId = useId()
  const adjustTabId = useId()
  const tabPanelId = useId()

  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [tab, setTab] = useState<EditorTab>('adjust')

  const [cropEnabled, setCropEnabled] = useState(true)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [aspectKey, setAspectKey] = useState<string>('original')
  const [areaPixels, setAreaPixels] = useState<Area | null>(null)
  const [mediaSize, setMediaSize] = useState<{
    naturalWidth: number
    naturalHeight: number
  } | null>(null)

  const [adjustments, setAdjustments] = useState<Adjustments>(DEFAULT_ADJUSTMENTS)
  const [output, setOutput] = useState<FormatOutput>('jpeg')
  const [result, setResult] = useState<GeneratedAsset | null>(null)

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  const hasImage = !!preview

  const originalAspect = mediaSize ? mediaSize.naturalWidth / mediaSize.naturalHeight : undefined
  const aspect =
    aspectKey === 'original'
      ? originalAspect
      : FIXED_ASPECTS.find((item) => item.key === aspectKey)?.value

  const canSave =
    !!file &&
    !!businessId &&
    !editImage.isPending &&
    (!cropEnabled || (!!areaPixels && !!mediaSize))

  const cssFilter = `brightness(${adjustments.brightness}) saturate(${adjustments.saturation}) contrast(${adjustments.contrast})${adjustments.grayscale ? ' grayscale(1)' : ''}`

  function loadFile(picked: File | null) {
    setFile(picked)
    setPreview(picked ? URL.createObjectURL(picked) : null)
    setResult(null)
    setCropEnabled(true)
    setZoom(1)
    setCrop({ x: 0, y: 0 })
    setAspectKey('original')
    setAreaPixels(null)
    setMediaSize(null)
    setAdjustments(DEFAULT_ADJUSTMENTS)
    setTab('adjust')
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    loadFile(e.target.files?.[0] ?? null)
  }

  function toFraction(value: number): number {
    return Math.min(Math.max(value, 0), 1)
  }

  async function handleSave() {
    if (!file) return
    let cropRect: CropRect | undefined
    if (cropEnabled) {
      if (!areaPixels || !mediaSize) return
      cropRect = {
        left: toFraction(areaPixels.x / mediaSize.naturalWidth),
        top: toFraction(areaPixels.y / mediaSize.naturalHeight),
        width: toFraction(areaPixels.width / mediaSize.naturalWidth),
        height: toFraction(areaPixels.height / mediaSize.naturalHeight),
      }
    }
    const params: EditImageRequest = {
      ...(cropRect ? { crop: cropRect } : {}),
      ...adjustments,
      output,
    }
    try {
      const res = await editImage.mutateAsync({ file, params })
      setResult(res.asset)
      toast.success(m.editDone())
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  return (
    <div className="space-y-6">
      <AiProcessingOverlay open={editImage.isPending} />
      <PageHeader title={m.editPicture()} description={m.editPicturePageDescription()} />

      <Card>
        <CardContent className="space-y-5 pt-6">
          <div className="relative flex h-80 items-center justify-center overflow-hidden rounded-lg bg-muted">
            {preview ? (
              <Cropper
                image={preview}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                showGrid={cropEnabled}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_area, areaPx) => setAreaPixels(areaPx)}
                onMediaLoaded={(media) =>
                  setMediaSize({
                    naturalWidth: media.naturalWidth,
                    naturalHeight: media.naturalHeight,
                  })
                }
                style={{ mediaStyle: { filter: cssFilter } }}
              />
            ) : (
              <div className="w-full max-w-sm p-6">
                <ImageUploadField file={file} preview={null} onSelect={loadFile} />
              </div>
            )}
          </div>

          {hasImage ? (
            <fieldset className="min-w-0 space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <div role="tablist" aria-label={m.editPicture()} className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    role="tab"
                    id={adjustTabId}
                    aria-selected={tab === 'adjust'}
                    aria-controls={tabPanelId}
                    variant={tab === 'adjust' ? 'default' : 'outline'}
                    onClick={() => setTab('adjust')}
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    {m.editTabAdjust()}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    role="tab"
                    id={cropTabId}
                    aria-selected={tab === 'crop'}
                    aria-controls={tabPanelId}
                    variant={tab === 'crop' ? 'default' : 'outline'}
                    onClick={() => setTab('crop')}
                  >
                    <Crop className="h-4 w-4" />
                    {m.editTabCrop()}
                  </Button>
                </div>
                {hasImage && (
                  <div className="ml-auto flex items-center gap-1">
                    <AssetLibraryPicker
                      onSelect={(picked) => loadFile(picked)}
                      trigger={
                        <Button type="button" size="sm" variant="ghost">
                          <Images className="h-4 w-4" />
                          {m.chooseFromLibrary()}
                        </Button>
                      }
                    />
                    <Button asChild size="sm" variant="ghost" className="cursor-pointer">
                      <label>
                        <ImageUp className="h-4 w-4" />
                        {m.editReplace()}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          className="hidden"
                          onChange={handleFile}
                        />
                      </label>
                    </Button>
                  </div>
                )}
              </div>

              <div
                role="tabpanel"
                id={tabPanelId}
                aria-labelledby={tab === 'crop' ? cropTabId : adjustTabId}
              >
                {tab === 'crop' ? (
                  <div className="space-y-5">
                    <Label className="flex items-center gap-2">
                      <Checkbox
                        checked={cropEnabled}
                        onCheckedChange={(v) => setCropEnabled(v === true)}
                      />
                      {m.editCropEnable()}
                    </Label>

                    <fieldset
                      disabled={!cropEnabled}
                      className="min-w-0 space-y-5 disabled:opacity-60"
                    >
                      <div className="space-y-2">
                        <Label>{m.cropZoom()}</Label>
                        <input
                          type="range"
                          min={1}
                          max={3}
                          step={0.01}
                          value={zoom}
                          onChange={(e) => setZoom(Number(e.target.value))}
                          className="w-full accent-brand"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>{m.cropAspect()}</Label>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant={aspectKey === 'original' ? 'default' : 'outline'}
                            onClick={() => setAspectKey('original')}
                          >
                            {m.cropAspectOriginal()}
                          </Button>
                          {FIXED_ASPECTS.map((item) => (
                            <Button
                              key={item.key}
                              type="button"
                              size="sm"
                              variant={aspectKey === item.key ? 'default' : 'outline'}
                              onClick={() => setAspectKey(item.key)}
                            >
                              {item.label()}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </fieldset>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="flex flex-wrap gap-2">
                      {PRESETS.map((preset) => (
                        <Button
                          key={preset.key}
                          type="button"
                          size="sm"
                          variant={
                            sameAdjustments(adjustments, preset.value) ? 'default' : 'outline'
                          }
                          onClick={() => setAdjustments(preset.value)}
                        >
                          {preset.label()}
                        </Button>
                      ))}
                    </div>

                    <Slider
                      label={m.filterBrightness()}
                      min={0.5}
                      max={1.5}
                      value={adjustments.brightness}
                      onChange={(v) => setAdjustments((s) => ({ ...s, brightness: v }))}
                    />
                    <Slider
                      label={m.filterContrast()}
                      min={0.5}
                      max={1.5}
                      value={adjustments.contrast}
                      onChange={(v) => setAdjustments((s) => ({ ...s, contrast: v }))}
                    />
                    <Slider
                      label={m.filterSaturation()}
                      min={0}
                      max={2}
                      value={adjustments.saturation}
                      onChange={(v) => setAdjustments((s) => ({ ...s, saturation: v }))}
                    />

                    <div className="flex flex-wrap items-center gap-4">
                      <Label className="flex items-center gap-2">
                        <Checkbox
                          checked={adjustments.grayscale}
                          onCheckedChange={(v) =>
                            setAdjustments((s) => ({ ...s, grayscale: v === true }))
                          }
                        />
                        {m.filterGrayscale()}
                      </Label>
                      <Label className="flex items-center gap-2">
                        <Checkbox
                          checked={adjustments.normalize}
                          onCheckedChange={(v) =>
                            setAdjustments((s) => ({ ...s, normalize: v === true }))
                          }
                        />
                        {m.filterNormalize()}
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground">{m.editNormalizeHint()}</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>{m.formatConverterOutput()}</Label>
                <div className="flex gap-2">
                  {OUTPUTS.map((value) => (
                    <Button
                      key={value}
                      type="button"
                      size="sm"
                      variant={output === value ? 'default' : 'outline'}
                      onClick={() => setOutput(value)}
                    >
                      {value.toUpperCase()}
                    </Button>
                  ))}
                </div>
              </div>
            </fieldset>
          ) : null}

          {hasImage ? (
            <div className="flex justify-end">
              <Button disabled={!canSave} onClick={handleSave}>
                {editImage.isPending ? (
                  <PulsifyLoader className="size-5" tone="on-brand" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {editImage.isPending ? m.editSaving() : m.editSave()}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <div className="flex items-center justify-between">
              <p className="font-medium">{m.formatConverterResults()}</p>
              <Button asChild variant="link" size="sm">
                <Link to="/dashboard/media">{m.mediaLibrary()}</Link>
              </Button>
            </div>
            <div className="relative inline-block overflow-hidden rounded-lg bg-muted">
              <img src={result.url} alt="" className="max-h-64 object-contain" />
              <Button asChild size="icon" variant="secondary" className="absolute right-1 bottom-1">
                <a href={result.url} download target="_blank" rel="noreferrer">
                  <Download className="h-4 w-4" />
                </a>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {result.width}×{result.height}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function Slider({
  label,
  min,
  max,
  value,
  onChange,
}: {
  label: string
  min: number
  max: number
  value: number
  onChange: (v: number) => void
}) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <Label>{label}</Label>
        <span className="text-muted-foreground">{value.toFixed(2)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={0.01}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-brand"
      />
    </div>
  )
}
